import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { manifestActions } from '../_actions';
import { config, authHeader } from '../_helpers';
import CustomToolTip from '../_components/CustomToolTip/CustomToolTip';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { resourceService, manifestService } from '../_services';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { isManifestEditable } from '../_utils/utils';
import './ListManifests.css';
import '../assets/styles/react-datepicker-modal.css';

const RESOURCE_ORDER_FILE_TYPE = {
  CREATE_FORM: 1,
  UPLOADED_FORM: 2,
};

class ListManifests extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      manifestData: [],
      manifestPage: 1,
      manifestPerPage: 15,
      totalPages: 1,
      searchTerm: '',
      viewAllFilter: 'all',
      resourceName: '',
      resourceAvailabilityStatus: 'available', // Default to available
      loading: false,
      error: null,
      searchTimeout: null,
      action_id: '',
      viewStatus: false,
      showManifestModal: false,
      deleteManifestId: null,
      alertPop: false,
      alertHead: '',
      alertMsg: '',
      alertFunction: '',
      showVersionHistory: false,
      filterBy: 'select',
      dateRangeType: 'single',
      startDate: null,
      endDate: null,
    };

    this.manifestTableRef = React.createRef();
    this.handleSearch = this.handleSearch.bind(this);
    this.handleViewAllChange = this.handleViewAllChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.getManifestList = this.getManifestList.bind(this);
    this.viewAction = this.viewAction.bind(this);
    this.editManifest = this.editManifest.bind(this);
    this.printManifest = this.printManifest.bind(this);
    this.viewManifest = this.viewManifest.bind(this);
    this.handleToggleVersionHistory =
      this.handleToggleVersionHistory.bind(this);
    this.handleFilterByChange = this.handleFilterByChange.bind(this);
    this.handleDateRangeTypeChange = this.handleDateRangeTypeChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
  }
  componentDidMount() {
    this.getManifestList();
    this.getResourceName();
  }

  componentDidUpdate(prevProps) {
    // Update manifest data when Redux state changes
    if (prevProps.manifest !== this.props.manifest) {
      const { ManifestListV1, loading, error } = this.props.manifest;

      // Handle the V1 API response structure: { status: 'success', data: { data: [...], pagination: {...}, resource_info: {...}, summary: {...} } }
      if (
        ManifestListV1 &&
        ManifestListV1.data &&
        ManifestListV1.data.data &&
        Array.isArray(ManifestListV1.data.data)
      ) {
        const totalPages = ManifestListV1.data.pagination?.last_page || 1;
        this.setState({
          manifestData: ManifestListV1.data.data,
          totalPages: totalPages,
          loading: false,
          error: null,
        });
      } else if (
        ManifestListV1 &&
        ManifestListV1.data &&
        Array.isArray(ManifestListV1.data)
      ) {
        // Fallback: direct data array
        const totalPages = ManifestListV1.pagination?.last_page || 1;
        this.setState({
          manifestData: ManifestListV1.data,
          totalPages: totalPages,
          loading: false,
          error: null,
        });
      }
    }
  }

  componentWillUnmount() {
    // Clear search timeout to prevent memory leaks
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout);
    }
  }

  getResourceName() {
    // Get resource name from props or API
    if (this.props.props && this.props.props.resource_name) {
      this.setState({ resourceName: this.props.props.resource_name });
    } else if (this.props.resourceId) {
      // Fetch resource details from API
      manifestService
        .getResourceDetails(this.props.resourceId)
        .then((response) => {
          if (response && response.status === 'success' && response.data) {
            this.setState({
              resourceName: response.data.resource_name || 'Resource',
              resourceAvailabilityStatus:
                response.data.resource_availability_status || 'available',
            });
          }
        })
        .catch(() => {
          this.setState({
            resourceName: 'Resource',
            resourceAvailabilityStatus: 'available',
          });
        });
    }
  }

  getManifestList = (manifestPageNum = 1) => {
    const data = {
      resourceId: parseInt(this.props.resourceId),
      perPage: this.state.manifestPerPage,
      page: manifestPageNum,
    };

    // Add incidentName to the request if search term exists
    if (this.state.searchTerm && this.state.searchTerm.trim() !== '') {
      data.incidentName = this.state.searchTerm.trim();
    }

    // Add manifestAvailabilityStatus to the request if filter is not "all"
    if (this.state.viewAllFilter && this.state.viewAllFilter !== 'all') {
      data.manifestAvailabilityStatus = this.state.viewAllFilter;
    }

    // Add date filters based on filterBy selection
    if (this.state.filterBy !== 'select' && this.state.startDate) {
      const formattedStartDate = moment(this.state.startDate).format(
        'YYYY-MM-DD',
      );
      const formattedEndDate = this.state.endDate
        ? moment(this.state.endDate).format('YYYY-MM-DD')
        : formattedStartDate;

      if (this.state.filterBy === 'date_added') {
        data.date_added_from = formattedStartDate;
        if (this.state.dateRangeType === 'range' && this.state.endDate) {
          data.date_added_to = formattedEndDate;
        } else if (this.state.dateRangeType === 'single') {
          // For single date, use same date for from and to
          data.date_added_to = formattedStartDate;
        }
      } else if (this.state.filterBy === 'date_assigned') {
        data.date_assigned_from = formattedStartDate;
        if (this.state.dateRangeType === 'range' && this.state.endDate) {
          data.date_assigned_to = formattedEndDate;
        } else if (this.state.dateRangeType === 'single') {
          // For single date, use same date for from and to
          data.date_assigned_to = formattedStartDate;
        }
      }
    }

    this.props.dispatch(manifestActions.manifestListV1(data));
  };

  handleSearch = (event) => {
    const searchTerm = event.target.value;
    this.setState({ searchTerm });

    // Clear existing timeout
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      this.getManifestList(1); // Reset to page 1 when searching
    }, 500); // 500ms delay

    this.setState({ searchTimeout: timeout });
  };

  handleViewAllChange = (event) => {
    const filter = event.target.value;
    this.setState({ viewAllFilter: filter }, () => {
      // Trigger API call with new filter
      this.getManifestList(1); // Reset to page 1 when filter changes
    });
  };

  handleToggleVersionHistory = () => {
    this.setState({ showVersionHistory: !this.state.showVersionHistory });
  };

  handleFilterByChange = (event) => {
    const filter = event.target.value;
    this.setState({ filterBy: filter }, () => {
      // Only trigger API call if we have a date selected or if reverting to 'select'
      if (filter === 'select' || this.state.startDate) {
        this.getManifestList(1); // Reset to page 1 when filter changes
      }
    });
  };

  handleDateRangeTypeChange = (event) => {
    const newType = event.target.value;
    this.setState({ dateRangeType: newType }, () => {
      // Clear end date when switching to single date
      if (newType === 'single') {
        this.setState({ endDate: null }, () => {
          if (this.state.filterBy !== 'select' && this.state.startDate) {
            this.getManifestList(1);
          }
        });
      }
    });
  };

  handleStartDateChange = (date) => {
    this.setState({ startDate: date }, () => {
      // Trigger API call if filter is selected
      if (this.state.filterBy !== 'select') {
        this.getManifestList(1); // Reset to page 1 when date changes
      }
    });
  };

  handleEndDateChange = (date) => {
    this.setState({ endDate: date }, () => {
      // Trigger API call if filter is selected and in range mode
      if (
        this.state.filterBy !== 'select' &&
        this.state.dateRangeType === 'range'
      ) {
        this.getManifestList(1); // Reset to page 1 when date changes
      }
    });
  };

  handleClearFilters = () => {
    this.setState(
      {
        filterBy: 'select',
        dateRangeType: 'single',
        startDate: null,
        endDate: null,
      },
      () => {
        // Refresh manifest list without date filters
        this.getManifestList(1);
      },
    );
  };

  handlePageChange = (newPage) => {
    this.setState({ manifestPage: newPage }, () => {
      this.getManifestList(newPage);
      // Scroll to top of manifest table
      if (this.manifestTableRef.current) {
        this.manifestTableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    });
  };

  formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  viewAction = (id) => {
    // If clicking the same item, toggle close; otherwise open the new one
    if (this.state.action_id === id && this.state.viewStatus) {
      this.setState({ viewStatus: false });
    } else {
      this.setState({
        action_id: id,
        viewStatus: true,
      });
    }
  };

  editManifest = (id, baseManifestId) => {
    this.props.editManifest(id, baseManifestId);
  };

  printManifest = (id) => {
    var data = '?manifest_id=' + id + '&resource_id=' + this.props.resourceId;
    const requestOptions = {
      method: 'POST',
      headers: authHeader(),
    };
    var self = this;
    fetch(config.apiUrl + '/api/manifest/pdf' + data, requestOptions)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data.success) {
          const pdfLink = data.data;
          var link = document.createElement('a');
          if (
            window.navigator.userAgent.match(/iPad/i) ||
            window.navigator.userAgent.match(/iPhone/i)
          ) {
            link.href = pdfLink;
          } else {
            link.href = pdfLink;
            link.target = '_blank';
          }
          document.body.appendChild(link);
          link.click();
          setTimeout(function () {
            document.body.removeChild(link);
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(pdfLink);
          }, 100);
        } else {
          self.setState({
            alertPop: true,
            alertHead: 'Error',
            alertMsg: data.message,
            alertFunction: '',
          });
        }
      });
  };

  viewManifest = (id, baseManifestId) => {
    this.props.viewManifest(id, baseManifestId.toString());
  };

  handleViewResourceOrderForm(manifest) {
    try {
      const resourceOrderFormType =
        manifest.resourceOrderFormType || manifest.resource_order_form_type;
      const resourceOrderForm =
        manifest.resourceOrderForm || manifest.resource_order_form;

      switch (resourceOrderFormType) {
        case RESOURCE_ORDER_FILE_TYPE?.UPLOADED_FORM:
          if (resourceOrderForm) {
            window.open(resourceOrderForm, '_blank').focus();
          } else {
            toast.error('Resource order form file not found');
          }
          break;
        case RESOURCE_ORDER_FILE_TYPE?.CREATE_FORM:
          this.printResourceOrderForm(manifest);
          break;
        default:
          // Try to handle as UPLOADED_FORM if resourceOrderForm exists
          if (resourceOrderForm) {
            window.open(resourceOrderForm, '_blank').focus();
          } else {
            console.warn(
              'Unknown resource order form type:',
              resourceOrderFormType,
            );
            toast.error('Unable to view resource order form');
          }
          break;
      }
    } catch (error) {
      console.error('Error viewing resource order form:', error);
      toast.error('Error viewing resource order form');
    }
  }

  printResourceOrderForm(manifest) {
    try {
      let data = new FormData();
      const manifestId =
        manifest.manifestId || manifest.manifest_id || manifest.mid;
      data.append('manifest_id', manifestId);

      manifestService
        ?.viewCreatedResourceOrderForm(data)
        .then((response) => {
          this?.redirectToPdf(response);
        })
        .catch((error) => {
          console.error('Error fetching resource order form:', error);
          toast.error('Error loading resource order form');
        });
    } catch (error) {
      console.error('Error in printResourceOrderForm:', error);
      toast.error('Error generating resource order form');
    }
  }

  redirectToPdf = (data) => {
    if (data?.success) {
      const pdfLink = data.data;
      var link = document.createElement('a');
      if (
        window.navigator.userAgent.match(/iPad/i) ||
        window.navigator.userAgent.match(/iPhone/i)
      ) {
        link.href = pdfLink;
      } else {
        link.href = pdfLink;
        link.target = '_blank';
      }
      document.body.appendChild(link);
      link.click();
      setTimeout(function () {
        document.body.removeChild(link);
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(pdfLink);
      }, 100);
    }
  };

  deleteCloseModal = () => {
    this.setState({ showManifestModal: false, deleteManifestId: null });
  };

  deleteManifest = async () => {
    let data = new FormData();
    data.append('manifest_id', this.state.deleteManifestId);
    let data2 = new FormData();
    data2.append('resource_id', this.props.resourceId);
    await this.props.dispatch(manifestActions.deleteManifest(data, data2));
    this.getManifestList(); // Refresh the manifest list
    this.setState({ showManifestModal: false, deleteManifestId: null });
  };

  renderManifestRows = () => {
    const { manifestData, searchTerm, viewAllFilter, showVersionHistory } =
      this.state;

    // Ensure manifestData is an array
    let manifests = Array.isArray(manifestData) ? manifestData : [];

    // If we have no data, show a simple test row
    if (manifests.length === 0) {
      return <div> No manifests found </div>;
    }

    /**
     * Get CSS class name for manifest status
     */
    const getStatusClass = (status) => {
      switch (status) {
        case 'inuse':
          return 'inuse';
        case 'closedout':
          return 'closedout';
        case 'pending':
          return 'pending';
        default:
          return 'default';
      }
    };

    /**
     * Get human-readable status label
     */
    const getStatusLabel = (status) => {
      switch (status) {
        case 'inuse':
          return 'In Use';
        case 'closedout':
          return 'Closed Out';
        case 'pending':
          return 'Pending';
        default:
          return 'Available';
      }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    };

    // If we have data but it's not in the expected format, render it directly
    if (
      manifests.length > 0 &&
      !manifests[0].contract_number &&
      !manifests[0].abbreviation
    ) {
      return manifests.map((manifest, index) => (
        <div className='manifest_items' key={`raw-${index}`}>
          <CustomToolTip
            label={getStatusLabel(manifest.manifest_availability_status)}
            customClass={'fs-tooltip'}
          >
            <div
              className={`employee_item manifest-row manifest-status-${getStatusClass(manifest.manifest_availability_status)}`}
            >
              <div className='list_item'>
                {manifest.abbreviation || manifest.contract_type || 'N/A'}
              </div>
              <div className='list_item'>
                {manifest.contract_number || manifest.contractNumber || 'N/A'}
              </div>
              <div className='list_item'>
                <div className='incident_name_wrapper'>
                  <div className='incident_name'>
                    {manifest.incident_name || manifest.incidentName || 'N/A'}
                  </div>
                </div>
              </div>
              <div className='list_item'>
                {manifest.resource_name || manifest.resourceNumber || 'N/A'}
              </div>
              <div className='list_item'>
                {manifest.resource_order_form_exists === 1 &&
                manifest.resource_order_name ? (
                  <CustomToolTip
                    label={manifest.resource_order_name}
                    customClass={'fs-tooltip'}
                  >
                    <a
                      href='#'
                      className='view-ro-link'
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        this.handleViewResourceOrderForm(manifest);
                      }}
                    >
                      View RO
                    </a>
                  </CustomToolTip>
                ) : (
                  '-'
                )}
              </div>
              <div className='list_item'>{manifest.added_date || 'N/A'}</div>
              <div className='list_item'>{manifest.date_assigned || 'N/A'}</div>
              <div className='list_item manifest-history-column'></div>
              <div className='list_item action_icon'>
                <div className='action-menu'>
                  <span className='action-dots'>⋮</span>
                </div>
              </div>
            </div>
          </CustomToolTip>
        </div>
      ));
    }

    const manifestsOption = [];

    // Wrap all rows in a single manifest_items container
    const allRows = [];

    manifests.forEach((item, manifestIndex) => {
      // Transform each manifest item
      const manifest = {
        manifestId: item.manifest_id || item.mid,
        abbreviation: item.abbreviation || '',
        contractNumber: item.contract_number || '-',
        incidentName: item.incident_name || '',
        resourceNumber: item.resource_number || '',
        addedDate: item.added_date || '',
        dateAssigned: item.date_assigned || '',
        resourceOrderFormExists: item.resource_order_form_exists,
        resourceOrderName: item.resource_order_name,
        assignmentStatus: item.assignment_status,
        manifestAvailability: item.manifest_availability,
        manifestStatus: item.manifest_status,
        baseManifestId: item.bid || item.base_manifest_id,
        hasLatestVersion: item.has_latest_version,
        resourceOrderFormType: item.resource_order_form_type,
        resourceOrderForm: item.resource_order_form,
        versions: item.versions || [],
      };

      // Determine if there are multiple versions
      const hasMultipleVersions = manifest.versions.length > 1;

      // Sort versions within this manifest (latest first)
      const sortedVersions = manifest.versions.sort((a, b) => {
        return new Date(b.added_date) - new Date(a.added_date);
      });

      // Determine which versions to show based on toggle
      const versionsToShow = showVersionHistory
        ? sortedVersions
        : sortedVersions.slice(0, 1);

      versionsToShow.forEach((version, versionIndex) => {
        const isLatestVersion = versionIndex === 0;

        // Render each manifest record independently
        allRows.push(
          <React.Fragment
            key={`${manifest.manifestId}-${version.id || versionIndex}-${manifestIndex}`}
          >
            <CustomToolTip
              label={getStatusLabel(manifest.manifestAvailability)}
              customClass={'fs-tooltip'}
            >
              <div
                className={`employee_item manifest-row manifest-status-${getStatusClass(
                  manifest.manifestAvailability,
                )}${showVersionHistory && hasMultipleVersions && !isLatestVersion ? ' old-version-row' : ''}`}
              >
                <div className='list_item'>{manifest.abbreviation}</div>
                <div className='list_item'>{manifest.contractNumber}</div>
                <div className='list_item'>
                  <div className='incident_name_wrapper'>
                    <div className='incident_name'>{manifest.incidentName}</div>
                  </div>
                </div>
                <div className='list_item'>{manifest.resourceNumber}</div>
                <div className='list_item'>
                  {manifest.resourceOrderFormExists === 1 &&
                  manifest.resourceOrderName ? (
                    <a
                      className='view-ro-link'
                      style={{ cursor: 'pointer' }}
                      href='#'
                      onClick={(e) => {
                        e.preventDefault();
                        this.handleViewResourceOrderForm(manifest);
                      }}
                    >
                      View RO
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
                <div className='list_item'>
                  {this.formatDate(version.added_date || manifest.addedDate)}
                </div>
                <div className='list_item'>
                  {version.date_assigned || manifest.dateAssigned
                    ? this.formatDate(
                        version.date_assigned || manifest.dateAssigned,
                      )
                    : '-'}
                </div>
                {showVersionHistory && (
                  <div className='list_item'>
                    <div
                      className={`version-item ${isLatestVersion ? 'latest-version' : 'old-version'}`}
                    >
                      <div className='version-date-time'>
                        {version.added_date}
                      </div>
                      {isLatestVersion && (
                        <div className='latest-label'>Latest version</div>
                      )}
                      <div className='version-user'>
                        {version.created_by_user_name}
                      </div>
                    </div>
                  </div>
                )}
                {!showVersionHistory && (
                  <div className='list_item manifest-history-column'>
                    {hasMultipleVersions && (
                      <img
                        src={
                          require('../assets/icons/history_24dp.svg').default
                        }
                        alt='View manifest'
                        className='manifest-history-icon'
                        onClick={() => {
                          this.viewManifest(
                            manifest.manifestId,
                            manifest.baseManifestId,
                          );
                        }}
                      />
                    )}
                  </div>
                )}
                <div
                  className='list_item action_icon'
                  onClick={() =>
                    this.viewAction(
                      `${manifest.manifestId}-${manifestIndex}-${versionIndex}`,
                    )
                  }
                ></div>
              </div>
            </CustomToolTip>
            {this.state.action_id ===
              `${manifest.manifestId}-${manifestIndex}-${versionIndex}` && (
              <div
                className={
                  this.state.viewStatus ? 'action_list' : 'action_list hide'
                }
              >
                {isLatestVersion &&
                  isManifestEditable(
                    manifest.manifestStatus,
                    this.state.resourceAvailabilityStatus,
                  ) && (
                    <p
                      onClick={() =>
                        this.editManifest(
                          manifest.manifestId,
                          manifest.baseManifestId,
                        )
                      }
                    >
                      Edit
                    </p>
                  )}
                <p onClick={() => this.printManifest(manifest.manifestId)}>
                  Print
                </p>
                <p
                  onClick={() =>
                    this.viewManifest(
                      manifest.manifestId,
                      manifest.baseManifestId,
                    )
                  }
                >
                  View
                </p>
                {isLatestVersion && (
                  <p
                    onClick={() =>
                      this.setState({
                        showManifestModal: true,
                        deleteManifestId: manifest.manifestId,
                      })
                    }
                  >
                    Delete
                  </p>
                )}
              </div>
            )}
          </React.Fragment>,
        );
      });
    });

    // Wrap all rows in manifest_items container
    if (allRows.length > 0) {
      manifestsOption.push(
        <div className='manifest_items' key='all-manifests-wrapper'>
          {allRows}
        </div>,
      );
    }

    return manifestsOption;
  };

  renderMobileManifestCards = () => {
    const { manifestData } = this.state;
    let manifests = Array.isArray(manifestData) ? manifestData : [];

    if (manifests.length === 0) {
      return null;
    }

    return (
      <div className='for_mobile_manifest_list_page'>
        {manifests.map((item, index) => {
          // Transform each manifest item
          const manifest = {
            manifestId: item.manifest_id || item.mid,
            abbreviation: item.abbreviation || '',
            contractNumber: item.contract_number || '-',
            incidentName: item.incident_name || '',
            resourceNumber: item.resource_number || '',
            addedDate: item.added_date || '',
            dateAssigned: item.date_assigned || '',
            resourceOrderFormExists: item.resource_order_form_exists,
            resourceOrderName: item.resource_order_name,
            assignmentStatus: item.assignment_status,
            manifestAvailability: item.manifest_availability,
            manifestStatus: item.manifest_status,
            baseManifestId: item.bid || item.base_manifest_id,
            hasLatestVersion: item.has_latest_version,
            resourceOrderFormType: item.resource_order_form_type,
            resourceOrderForm: item.resource_order_form,
            versions: item.versions || [],
          };

          // Determine if there are multiple versions
          const hasMultipleVersions = manifest.versions.length > 1;

          return (
            <React.Fragment key={index}>
              <hr />
              <div className='manifest_items'>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Contract Type</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.abbreviation || '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Contract Number</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.contractNumber || '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Incident Name</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.incidentName || '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Resource Number</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.resourceNumber || '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>RO</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.resourceOrderName || '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Date Added</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.addedDate
                      ? this.formatDate(manifest.addedDate)
                      : '-'}
                  </div>
                </div>
                <div className='status_item'>
                  <div className='list_item'>
                    <strong>Date Assigned</strong>
                  </div>
                  <div className='list_item'>
                    {manifest.dateAssigned
                      ? this.formatDate(manifest.dateAssigned)
                      : '-'}
                  </div>
                </div>
                {hasMultipleVersions && (
                  <div className='status_item'>
                    <div className='list_item'>
                      <strong>History</strong>
                    </div>
                    <div className='list_item'>
                      <img
                        src={
                          require('../assets/icons/history_24dp.svg').default
                        }
                        alt='View manifest'
                        className='manifest-history-icon'
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          this.viewManifest(
                            manifest.manifestId,
                            manifest.baseManifestId,
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className='status_item mobile_action_row'>
                  <div
                    className='list_item action_icon mobile_action_icon'
                    onClick={() =>
                      this.viewAction(`${manifest.manifestId}-${index}`)
                    }
                  ></div>
                </div>
              </div>
              {this.state.action_id === `${manifest.manifestId}-${index}` && (
                <div
                  className={
                    this.state.viewStatus ? 'action_list' : 'action_list hide'
                  }
                >
                  {isManifestEditable(
                    manifest.manifestStatus,
                    this.state.resourceAvailabilityStatus,
                  ) && (
                    <p
                      onClick={() =>
                        this.editManifest(
                          manifest.manifestId,
                          manifest.baseManifestId,
                        )
                      }
                    >
                      Edit
                    </p>
                  )}
                  <p onClick={() => this.printManifest(manifest.manifestId)}>
                    Print
                  </p>
                  <p
                    onClick={() =>
                      this.setState({
                        showManifestModal: true,
                        deleteManifestId: manifest.manifestId,
                      })
                    }
                  >
                    Delete
                  </p>
                  <p
                    onClick={() =>
                      this.viewManifest(
                        manifest.manifestId,
                        manifest.baseManifestId,
                      )
                    }
                  >
                    View
                  </p>
                </div>
              )}
            </React.Fragment>
          );
        })}
        {this.renderPagination()}
      </div>
    );
  };

  renderPagination = () => {
    const { manifestPage, totalPages } = this.state;

    if (totalPages <= 1) {
      return null;
    }

    let pages = [];
    let currentPage = manifestPage;
    let startingIndex = currentPage - 2 > 0 ? currentPage - 2 : 1;
    let lastPageIndex =
      currentPage + 2 < totalPages ? currentPage + 2 : totalPages;
    let nextPage = currentPage + 1 > totalPages ? totalPages : currentPage + 1;
    let prevPage = currentPage - 1 > 0 ? currentPage - 1 : 1;

    // First page with ellipsis
    if (currentPage > 3) {
      pages.push(
        <li
          className='inactive'
          key={0}
          onClick={() => {
            this.handlePageChange(1);
          }}
        >
          <a>1</a>
        </li>,
      );
    }
    if (currentPage > 4) {
      pages.push(
        <li className='inactive' key={totalPages + 1}>
          <a>. . .</a>
        </li>,
      );
    }

    // Page numbers
    for (let i = startingIndex; i <= lastPageIndex; i++) {
      pages.push(
        <li
          className={i == currentPage ? 'active' : 'inactive'}
          key={i}
          onClick={() => {
            this.handlePageChange(i);
          }}
        >
          <a>{i}</a>
        </li>,
      );
    }

    // Last page with ellipsis
    if (lastPageIndex + 1 < totalPages) {
      pages.push(
        <li className='inactive' key={totalPages + 1}>
          <a>. . .</a>
        </li>,
      );
    }
    if (lastPageIndex < totalPages) {
      pages.push(
        <li
          className='inactive'
          key={totalPages}
          onClick={() => {
            this.handlePageChange(totalPages);
          }}
        >
          <a>{totalPages}</a>
        </li>,
      );
    }

    if (pages?.length > 0) {
      return (
        <div
          className='fs-pagination-wrapper-outer custome_old_pagination'
          key={7}
        >
          <div className='fs-pagination-wrapper'>
            <span
              className={`prev ${
                prevPage === 1 && currentPage === 1 ? 'disabled' : ''
              }`}
              onClick={() => {
                this.handlePageChange(prevPage);
              }}
            >
              <img
                alt='previous'
                src={require('../assets/icons/next_arrow.svg').default}
              />
            </span>
            <ul className='fs-pagination'>{pages}</ul>
            <span
              className={`next ${nextPage == totalPages ? 'disabled' : ''}`}
              onClick={() => {
                this.handlePageChange(nextPage);
              }}
            >
              <img
                alt='next'
                src={require('../assets/icons/next_arrow.svg').default}
              />
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  render() {
    const { resourceName, searchTerm, viewAllFilter } = this.state;
    const { loading } = this.props.manifest;

    return (
      <div className='resource_container resource_details'>
        <div className='breadcrumbs'>
          <Link className='green' to='/resources'>
            Resources
          </Link>{' '}
          /{' '}
          {this.props.resourceId && resourceName ? (
            <Link className='green' to={`/resources/${this.props.resourceId}`}>
              {resourceName}
            </Link>
          ) : (
            resourceName || '-'
          )}{' '}
          / Manifests
        </div>

        <div className='page_title' ref={this.manifestTableRef}>
          <h1>Manifests</h1>
        </div>

        <div className='manifest_controls'>
          <div className='view_all_dropdown'>
            <select
              id='viewAllFilter'
              name='viewAllFilter'
              value={viewAllFilter}
              onChange={this.handleViewAllChange}
              className='view_all_select'
            >
              <option value='all'>View All</option>
              <option value='inuse'>In Use</option>
              <option value='closedout'>Closed Out</option>
              <option value='pending'>Manifest Pending</option>
            </select>
          </div>

          <div className='manifest_controls_right'>
            <div className='search_field'>
              <input
                type='text'
                id='searchTerm'
                name='searchTerm'
                placeholder='Search by incident name or resource number...'
                value={searchTerm}
                onChange={this.handleSearch}
                className='search_input'
              />
            </div>
          </div>
        </div>

        <div className='manifest_divider'></div>

        <div className='manifest_filter_row'>
          <div className='filter_by_section'>
            <label className='filter_label'>Filter by</label>
            <select
              id='filterBy'
              name='filterBy'
              value={this.state.filterBy}
              onChange={this.handleFilterByChange}
              className='filter_dropdown'
            >
              <option value='select'>Select</option>
              <option value='date_added'>Date Added</option>
              <option value='date_assigned'>Date Assigned</option>
            </select>
            {this.state.filterBy !== 'select' && (
              <>
                <select
                  id='dateRangeType'
                  name='dateRangeType'
                  value={this.state.dateRangeType}
                  onChange={this.handleDateRangeTypeChange}
                  className='filter_dropdown'
                >
                  <option value='single'>Single Date</option>
                  <option value='range'>Date Range</option>
                </select>
                <span className='date-picker-wrapper'>
                  <span className='datepicker-image'>
                    <img
                      src={
                        require('../assets/images/icon_calendar.svg').default
                      }
                      alt='calendar'
                    />
                  </span>
                  <DatePicker
                    placeholderText='mm/dd/yyyy'
                    selected={this.state.startDate}
                    onChange={this.handleStartDateChange}
                    className='date_input'
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode='select'
                    onKeyDown={(e) => e.preventDefault()}
                  />
                </span>
                {this.state.dateRangeType === 'range' && (
                  <>
                    <span className='date_separator'>to</span>
                    <span className='date-picker-wrapper'>
                      <span className='datepicker-image'>
                        <img
                          src={
                            require('../assets/images/icon_calendar.svg')
                              .default
                          }
                          alt='calendar'
                        />
                      </span>
                      <DatePicker
                        placeholderText='mm/dd/yyyy'
                        selected={this.state.endDate}
                        onChange={this.handleEndDateChange}
                        className='date_input'
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode='select'
                        minDate={this.state.startDate}
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    </span>
                  </>
                )}
                <button
                  className='clear_filters'
                  onClick={this.handleClearFilters}
                >
                  Clear
                </button>
              </>
            )}
          </div>

          <div className='version_history_toggle'>
            <label htmlFor='showVersionHistory'>Show Version History</label>
            <div
              className={`toggle_switch ${this.state.showVersionHistory ? 'active' : ''}`}
              onClick={this.handleToggleVersionHistory}
            >
              <div className='toggle_slider' />
            </div>
          </div>
        </div>

        <div className='mobile_hide_list_manifests'>
          <div
            className={`manifest_list outer_list ${this.state.showVersionHistory ? 'show-version' : ''}`}
          >
            <div className='list_header'>
              <div className='list_item_head'>Contract Type</div>
              <div className='list_item_head'>Contract Number</div>
              <div className='list_item_head'>Incident Name</div>
              <div className='list_item_head'>Resource Number</div>
              <div className='list_item_head'>RO</div>
              <div className='list_item_head'>Date Added</div>
              <div className='list_item_head'>Date Assigned</div>
              {this.state.showVersionHistory && (
                <div className='list_item_head'>Version</div>
              )}
              {!this.state.showVersionHistory && (
                <div className='list_item_head' style={{ width: '40px' }}></div>
              )}
              <div className='list_item_head' />
            </div>

            {loading ? (
              <div className='loading'>Loading...</div>
            ) : (
              <>
                {this.renderManifestRows()}
                {this.renderPagination()}
              </>
            )}
          </div>
        </div>
        {this.renderMobileManifestCards()}

        {/* Delete Manifest Modal */}
        <ReactModal
          isOpen={this.state.showManifestModal}
          onClose={this.deleteCloseModal}
          overlayClassName='deletetPopup'
        >
          <div id='deletePopup'>
            <div className='modal-head'>
              <h4 className='modal-title'>Confirm</h4>
              <img
                alt='close'
                className='closeModal'
                onClick={this.deleteCloseModal}
                src={require('../assets/images/close_green.png').default}
              />
            </div>
            <div className='modal-body'>
              <div className='status_step step1'>
                <label>
                  Do you really want to remove this manifest? Doing so will
                  remove the assignment history and modify the employees' fire
                  records.
                </label>
                <br />
                <div className='btn_list_popup'>
                  <button
                    className='button grey'
                    onClick={this.deleteCloseModal}
                  >
                    Cancel
                  </button>
                  <button className='button' onClick={this.deleteManifest}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ReactModal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { manifest } = state;
  return { manifest };
}

const connectedListManifests = connect(mapStateToProps)(ListManifests);
export { connectedListManifests as ListManifests };

ListManifests.propTypes = {
  goBack: PropTypes.func.isRequired,
  resourceId: PropTypes.string.isRequired,
  props: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  manifest: PropTypes.object.isRequired,
  viewManifest: PropTypes.func.isRequired,
  editManifest: PropTypes.func.isRequired,
};
